import { QRCodeSVG } from "qrcode.react";
import { QrCode, Apple } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { PlayGlyph } from "@/components/Glyphs";
import { PLAY_STORE_URL, HERO_IMG, PREVIEWS } from "@/data/site";

const SECURE_IMG = PREVIEWS.find((p) => p.title.includes("Secure"))?.img || HERO_IMG;

const Phone = ({ src, className = "", alt }) => (
  <div className={`absolute w-[200px] sm:w-[230px] ${className}`}>
    <div className="overflow-hidden rounded-[2rem] border border-border bg-card shadow-2xl ring-1 ring-white/5">
      <img src={src} alt={alt} className="w-full" loading="lazy" />
    </div>
  </div>
);

export default function DownloadBand() {
  return (
    <section id="download" className="relative scroll-mt-24 overflow-hidden py-20 sm:py-28" data-testid="download-band">
      <div className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-b from-transparent via-sage/[0.05] to-transparent" />
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-2">
        {/* Phone mockups */}
        <Reveal className="relative mx-auto h-[430px] w-full max-w-md lg:order-1">
          <div className="absolute left-1/2 top-1/2 -z-10 h-72 w-72 -translate-x-1/2 -translate-y-1/2 animate-glow-pulse rounded-full bg-sage/20 blur-3xl" />
          <Phone
            src={HERO_IMG}
            alt="Warranty_Vault pro dashboard screen"
            className="left-2 top-10 -rotate-6 opacity-95 sm:left-6"
          />
          <Phone
            src={SECURE_IMG}
            alt="Warranty_Vault pro security screen"
            className="right-2 top-0 rotate-6 sm:right-6"
          />
        </Reveal>

        {/* CTA */}
        <Reveal className="lg:order-2">
          <p className="eyebrow">Get the app</p>
          <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">
            Download <span className="text-gold">Warranty_Vault pro</span>
          </h2>
          <p className="mt-4 max-w-md text-muted-foreground">
            Scan the code with your phone to install it from Google Play, or tap the button below. Your whole vault, protected and in your pocket.
          </p>

          <div className="mt-8 flex flex-col items-center gap-6 rounded-2xl border border-border bg-card/50 p-6 backdrop-blur sm:flex-row sm:items-center">
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="download-qr-link"
              aria-label="Scan to download Warranty_Vault pro on Google Play"
              className="shrink-0 rounded-xl bg-white p-3 shadow-lg transition-transform duration-300 hover:scale-[1.03]"
            >
              <QRCodeSVG value={PLAY_STORE_URL} size={132} bgColor="#ffffff" fgColor="#0f1f17" level="M" includeMargin={false} />
            </a>

            <div className="flex flex-col items-center gap-3 sm:items-start">
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-foreground">
                <QrCode className="h-4 w-4 text-gold" /> Scan to install on Android
              </span>
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="download-play-btn"
                className="group inline-flex items-center gap-3 rounded-xl border border-gold/40 bg-card/80 px-4 py-2.5 transition-all duration-300 hover:-translate-y-0.5 hover:border-gold hover:shadow-[0_8px_30px_-10px_rgba(212,175,55,0.5)]"
              >
                <PlayGlyph className="h-6 w-6 text-foreground" />
                <span className="flex flex-col leading-none text-left">
                  <span className="text-[10px] font-medium uppercase tracking-[0.18em] text-gold">Get it on</span>
                  <span className="mt-1 font-display text-base font-semibold text-foreground">Google Play</span>
                </span>
              </a>
              <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                <Apple className="h-3.5 w-3.5" /> iOS coming soon
              </span>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
